import React, { useEffect } from 'react';
import { F2110 } from '../../../services/p2201Api';
import { fmt, fmtInt } from '../helpers';

interface Tab3Props {
  formData: Partial<F2110>;
  isEditing: boolean;
  getField: (key: keyof F2110) => F2110[keyof F2110];
  setField: (key: keyof F2110, value: F2110[keyof F2110]) => void;
  recalcMasterTotals: () => void;
}

/** Tab 3: Amount + Memo (Page3 from ui_spec) */
const Tab3AmountMemo: React.FC<Tab3Props> = React.memo(({
  formData, isEditing, getField, setField, recalcMasterTotals,
}) => {
  // Recalculate on tab mount to keep display in sync (BR-05 Page3 Click)
  useEffect(() => {
    recalcMasterTotals();
  }, [recalcMasterTotals]);

  /** Handle transportation/freight change: recalc totals (BR-09/10) */
  const handleAmountChange = (key: 'f2110_23' | 'f2110_25', value: string) => {
    setField(key, parseFloat(value) || 0);
    // Recalc is triggered by useEffect via dependency on formData
  };

  /** Handle tax type change (BR-11) [T-F03] */
  const handleTaxTypeChange = (val: number) => {
    setField('f2110_18', val);
  };

  // Compute displayed pending amount (BR-12)
  const grandTotal = (getField('f2110_20') as number) || 0;
  const paidAmount = (getField('f2110_21') as number) || 0;
  const pendingAmount = grandTotal - paidAmount;

  return (
    <div className="tab-content-section">
      <div className="form-grid">
        {/* Tax type (Optiongroup1 → Radio.Group) (BR-11) */}
        <label>稅別</label>
        <div className="radio-group">
          <label className="radio-item">
            <input
              type="radio"
              name="taxType"
              value={1}
              checked={(getField('f2110_18') as number) === 1}
              onChange={() => handleTaxTypeChange(1)}
              disabled={!isEditing} data-contract-field="f2110_18"
            />
            含稅(5%)
          </label>
          <label className="radio-item">
            <input
              type="radio"
              name="taxType"
              value={2}
              checked={(getField('f2110_18') as number) !== 1}
              onChange={() => handleTaxTypeChange(2)}
              disabled={!isEditing} data-contract-field="f2110_18"
            />
            免稅
          </label>
        </div>

        {/* Detail sum (readonly) */}
        <label>明細金額</label>
        <div className="amount-display">{fmt(getField('f2110_17') as number)}</div>

        {/* Transportation fee (BR-09) [T-F03] */}
        <label>交通費</label>
        <div>
          <input
            type="number"
            value={getField('f2110_23') as number ?? ''}
            onChange={e => handleAmountChange('f2110_23', e.target.value)}
            disabled={!isEditing}
            style={{ width: 140, textAlign: 'right' }}
            step="0.01" data-contract-field="f2110_23"
          />
        </div>

        {/* Freight (BR-10) */}
        <label>運費</label>
        <div>
          <input
            type="number"
            value={getField('f2110_25') as number ?? ''}
            onChange={e => handleAmountChange('f2110_25', e.target.value)}
            disabled={!isEditing}
            style={{ width: 140, textAlign: 'right' }}
            step="0.01" data-contract-field="f2110_25"
          />
        </div>

        {/* Subtotal excl tax (readonly) */}
        <label>未稅總金額</label>
        <div className="amount-display">{fmt(getField('f2110_28') as number)}</div>

        {/* Tax amount (readonly) */}
        <label>稅額</label>
        <div className="amount-display">{fmtInt(getField('f2110_19') as number)}</div>

        {/* Grand total (readonly) */}
        <label data-contract-field="f2110_20">含稅總金額</label>
        <div className="amount-display" style={{ fontWeight: 600 }}>{fmt(getField('f2110_20') as number)}</div>

        {/* Paid amount (BR-12) */}
        <label>已收總金額</label>
        <div>
          <input
            type="number"
            value={getField('f2110_21') as number ?? ''}
            onChange={e => setField('f2110_21', parseFloat(e.target.value) || 0)}
            disabled={!isEditing}
            style={{ width: 140, textAlign: 'right' }}
            step="0.01" data-contract-field="f2110_21"
          />
        </div>

        {/* Pending amount (dynamic label from BR-12) */}
        <label data-contract-field="f2110_20">待收總金額</label>
        <div className="amount-display" style={{ color: pendingAmount > 0 ? '#cf1322' : '#389e0d' }}>
          {fmt(pendingAmount)}
        </div>

        {/* Invoice number (BR-04 area) */}
        <label>發票號碼</label>
        <div>
          <input
            type="text"
            value={String(getField('f2110_14') || '')}
            onChange={e => setField('f2110_14', e.target.value)}
            disabled={!isEditing}
            maxLength={12}
            style={{ width: 140 }} data-contract-field="f2110_14"
          />
        </div>
      </div>

      {/* Memo (Edit1 → textarea) [BR-13] */}
      <div className="memo-group" style={{ marginTop: 16 }}>
        <label>備註</label>
        <textarea
          value={String(getField('f2110_30') || '')}
          onChange={e => setField('f2110_30', e.target.value)}
          disabled={!isEditing}
          rows={8}
          style={{ width: '100%' }} data-contract-field="f2110_30"
        />
      </div>
    </div>
  );
});

Tab3AmountMemo.displayName = 'Tab3AmountMemo';
export default Tab3AmountMemo;
